import { type FC } from 'react';
import type { LegacyCardConversationHeaderProps } from 'src/modules/communications/types/legacy-conversation-card.type';
import { getLegacyCardAvatarText } from 'src/modules/communications/utils/legacy-conversation-card.util';
import { isTestConversationDocument } from 'src/modules/communications/utils/test-conversation.util';
import { Avatar } from 'src/modules/shared/components/atoms/avatar';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { TestConversationBadge } from '../../test-conversations/test-conversation-badge';

/** Renders the legacy conversation contact header and its existing controls. */
export const LegacyConversationCardHeader: FC<
  LegacyCardConversationHeaderProps
> = ({
  conversation,
  formattedTime,
  actionSlot,
  className = '',
  showTestBadge = true,
}) => {
  const showDesktopTooltip = useMediaQuery('(min-width: 640px)');
  const contact = conversation.contacts?.[0];
  const address = contact?.addresses?.[0];
  const fallbackConversationHeader = address?.address ?? 'Sin dirección';
  const fullName = formatFullName(contact?.firstName, contact?.lastName).trim();
  const contactDisplayName = formatFullName(
    contact?.firstName,
    contact?.lastName,
    fallbackConversationHeader
  );
  const avatarText = getLegacyCardAvatarText({
    fullName,
    fallbackValue: fallbackConversationHeader,
  });
  const isTestConversation = isTestConversationDocument(conversation);

  const titleNode = (
    <div className="flex min-w-0 items-center gap-2">
      <Text
        size="small"
        weight="semibold"
        className="truncate text-[15px] text-primary-800"
        title={contactDisplayName}
      >
        {contactDisplayName}
      </Text>
      {showTestBadge && isTestConversation && (
        <div className="flex-shrink-0">
          <TestConversationBadge />
        </div>
      )}
    </div>
  );

  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Avatar text={avatarText} size="xs" />
        <div className="min-w-0 flex-1">
          {showDesktopTooltip ? (
            <Tooltip content={contactDisplayName} placement="top" size="sm">
              <div className="min-w-0">{titleNode}</div>
            </Tooltip>
          ) : (
            titleNode
          )}
        </div>
      </div>

      <div className="ml-2 flex shrink-0 items-center gap-1.5">
        {formattedTime && (
          <Text
            size="tiny"
            color="secondary"
            className="whitespace-nowrap text-[10px] text-secondary-500"
            title={formattedTime}
          >
            {formattedTime}
          </Text>
        )}
        {actionSlot}
      </div>
    </div>
  );
};
